/* import __COLOCATED_TEMPLATE__ from './zendesk.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */

import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { TaskGenerator } from 'ember-concurrency';
import ajax from 'embercom/lib/ajax';
import type ArticleSyncSetting from 'embercom/models/articles/article-sync-setting';
import type HelpCenterSite from 'embercom/models/help-center-site';
import { isEmpty } from 'underscore';
import { isPresent } from '@ember/utils';
import { taskFor } from 'ember-concurrency-ts';
import { task } from 'ember-concurrency-decorators';
import {
  PreferredAuthMethod,
  SourceType,
  SyncBehaviorType,
} from 'embercom/models/import-service/import-source';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import { extractZendeskSubdomain } from 'embercom/lib/knowledge-hub/utils';
import { isValidEmail } from 'embercom/lib/email';

interface Args {
  modal: any;
  title?: string;
  onNavigationBack?: () => void;
  onNavigationNext?: (viewId?: string) => void;
  showBackButton?: boolean;
  onModalClose: () => void;
  isMigrating?: boolean;
  existingSyncSetting?: ArticleSyncSetting;
}

export default class ZendeskSyncModal extends Component<Args> {
  @service store: any;
  @service realTimeEventService: any;
  @service openCenteredWindowService: any;
  @service notificationsService: any;
  @service intl: any;
  @service helpCenterService: any;
  @service appService: any;
  @service regionService: any;
  @tracked url = '';
  @tracked selectedHelpCenter: HelpCenterSite | null = null;
  @tracked sync: boolean;
  @tracked existingImport = false;
  @tracked syncSettings: ArticleSyncSetting[] = [];
  @tracked syncPrivateArticles = false;
  @tracked syncStaffArticles = false;
  @tracked useOAuth = true;
  @tracked email = '';
  @tracked apiToken = '';
  @tracked temporarilyEnableNewImporter = false;
  @service declare intercomEventService: any;
  @service declare knowledgeHubService: KnowledgeHubService;

  constructor(owner: unknown, args: Args) {
    super(owner, args);

    this.realTimeEventService.on('ImportStarted', this, 'handleStartedImport');
    this.initHelpCenterService();
    taskFor(this.getRunningImport).perform();
    taskFor(this.setSyncSettings).perform();

    this.sync = this.appService.app.canUseStandalone;

    // Pre-fill form when migrating from old importer
    if (args.isMigrating && args.existingSyncSetting) {
      let siteUrl = args.existingSyncSetting.siteUrl || '';
      this.url = siteUrl.replace(/^https?:\/\//, '');
      this.syncPrivateArticles = args.existingSyncSetting.importPrivateArticles || false;
      this.syncStaffArticles = args.existingSyncSetting.importStaffArticles || false;
      this.sync = true;
      this.temporarilyEnableNewImporter = true;
    }
  }

  async initHelpCenterService() {
    // In case we are accessing this component from outside a route that initializes the help center service
    if (!this.helpCenterService.site) {
      await this.helpCenterService.forceFetchSites();
    }

    this.selectedHelpCenter = this.args.isMigrating
      ? this.helpCenters?.find(
          (helpCenter: HelpCenterSite) =>
            helpCenter.id === this.args.existingSyncSetting?.helpCenterId,
        ) || null
      : this.helpCenterService.site;
  }

  willDestroy() {
    super.willDestroy();
    this.realTimeEventService.off('ImportStarted', this, 'handleStartedImport');
  }

  @task({ drop: true })
  *setSyncSettings() {
    this.syncSettings = yield this.store.findAll('articles/article-sync-setting');
  }

  get hasActiveApiImport() {
    return isPresent(this.existingImport);
  }

  @task({ drop: true })
  *getRunningImport(): TaskGenerator<void> {
    this.existingImport = yield ajax({
      url: '/ember/article_import_jobs/current_import',
      type: 'GET',
      data: { app_id: this.app.id },
    });
  }

  get app() {
    return this.appService.app;
  }

  get helpCenters() {
    return this.helpCenterService.sites;
  }

  get helpCenterOptions() {
    return this.helpCenters?.map((site: HelpCenterSite) => {
      return {
        text: site.name,
        value: site.id,
      };
    });
  }

  get isUrlInvalid() {
    let normalizedUrl = this.normalizeZendeskURL(this.url);

    if (!normalizedUrl) {
      return false;
    }

    try {
      let parsedUrl = new URL(normalizedUrl);
      return !parsedUrl.hostname.match(/.+\.zendesk\.com$/);
    } catch (e) {
      return true;
    }
  }

  get errorMessage() {
    if (this.isUrlInvalid) {
      return this.intl.t('knowledge-hub.sync-modal.zendesk.error-message');
    }

    if (this.isExistingActiveZendeskSync) {
      return this.intl.t('knowledge-hub.sync-modal.zendesk.already-synced-subdomain');
    }
  }

  get isExistingActiveZendeskSync() {
    // Skip this check during migration since the domain should exist
    if (this.isMigrating) {
      return false;
    }
    return !!this.syncSettings.find((setting: ArticleSyncSetting) => {
      return (
        setting.provider === 'zendesk' &&
        setting.status === 'active' &&
        setting.siteUrl === this.normalizeZendeskURL(this.url)
      );
    });
  }

  get isEmailInvalid() {
    let trimmedEmail = this.email.trim();

    if (!trimmedEmail) {
      return false;
    }

    return !isValidEmail(trimmedEmail);
  }

  get emailErrorMessage() {
    if (this.isEmailInvalid) {
      return this.intl.t('knowledge-hub.sync-modal.zendesk.invalid-email');
    }
  }

  get syncBehavior() {
    return this.sync ? SyncBehaviorType.Sync : SyncBehaviorType.Import;
  }

  get syncButtonDisabled() {
    // During migration, skip the active import check because:
    // - We already check if the specific old sync being migrated is running via dropdown action's isSyncing
    // - hasActiveApiImport blocks if ANY Zendesk sync is running (not just the one being migrated)
    let hasBlockingImport = this.isMigrating ? false : this.hasActiveApiImport;

    let baseConditions =
      this.url.length === 0 ||
      hasBlockingImport ||
      this.isUrlInvalid ||
      this.isExistingActiveZendeskSync;

    if (this.useOAuth) {
      return baseConditions;
    } else {
      return (
        baseConditions ||
        this.email.trim().length === 0 ||
        this.isEmailInvalid ||
        this.apiToken.trim().length === 0
      );
    }
  }

  get isMigrating() {
    return this.args.isMigrating ?? false;
  }

  get syncButtonLabel() {
    if (this.isMigrating) {
      return this.intl.t('knowledge-hub.sync-modal.zendesk.migrate-button-label');
    }
    return this.intl.t(
      `knowledge-hub.sync-modal.behavior.${this.syncBehavior}.primary-button-label`,
    );
  }

  get syncButtonDisabledTooltip() {
    if (this.hasActiveApiImport) {
      return this.intl.t('knowledge-hub.sync-modal.zendesk.another-sync-in-progress');
    }

    if (this.url.length === 0 || this.isUrlInvalid) {
      return this.intl.t('knowledge-hub.sync-modal.zendesk.error-message');
    }

    if (this.isExistingActiveZendeskSync) {
      return this.intl.t('knowledge-hub.sync-modal.zendesk.already-synced-subdomain');
    }

    return;
  }

  handleStartedImport() {
    let notificationMessage = this.sync
      ? this.intl.t('knowledge-hub.sync-modal.zendesk.sync-started')
      : this.intl.t('knowledge-hub.sync-modal.zendesk.import-started');
    this.notificationsService.notifyConfirmation(notificationMessage);
    this.args.onModalClose();
  }

  get shouldAlwaysUseIntercomRedirectUri() {
    return (
      this.app.canUseStandalone &&
      this.app.canUseFinStandaloneRedirectFromSuite &&
      !this.app.canOptOutOfFinStandaloneRedirectFromSuite
    );
  }

  @action switchHelpCenter(value: string) {
    this.selectedHelpCenter = this.helpCenters.find(
      (helpCenter: HelpCenterSite) => helpCenter.id === value,
    );
  }

  @action
  enrollInNewImporter() {
    this.temporarilyEnableNewImporter = true;
  }

  @action
  async startSync() {
    let url = this.normalizeZendeskURL(this.url);
    if (!url) {
      return;
    }

    if (this.canUseNewImporter && !this.useOAuth) {
      await this.startSyncWithNewImporter(url, PreferredAuthMethod.ApiKey);
    } else {
      await this.startSyncWithOldImporter(url, this.canUseNewImporter); //uses old importer's OAuth flow to trigger new importer
    }
  }

  get canUseNewImporter() {
    return (
      this.temporarilyEnableNewImporter ||
      this.appService.app.canUseFeature('team-knowledge-foundations-zendesk-new-importer-ui')
    );
  }

  get shouldShowMigrationBanner() {
    return (
      this.appService.app.canUseFeature('team-knowledge-foundations-zendesk-migration-banner') &&
      !this.appService.app.canUseFeature('team-knowledge-foundations-zendesk-new-importer-ui') &&
      !this.temporarilyEnableNewImporter
    );
  }

  async startSyncWithNewImporter(url: string, preferredAuthMethod: PreferredAuthMethod) {
    let importSource;
    try {
      let subdomain = extractZendeskSubdomain(url);

      importSource = await this.knowledgeHubService.createImportSource({
        sourceType: SourceType.Zendesk,
        syncBehavior: this.syncBehavior,
        baseUrl: url,
        domain: subdomain,
        email: this.email.trim(),
        authToken: this.apiToken.trim(),
        preferredAuthMethod,
        syncPrivateArticles: this.syncPrivateArticles,
        syncStaffArticles: this.syncStaffArticles,
        helpCenterId: this.selectedHelpCenter?.id,
      });

      switch (preferredAuthMethod) {
        case PreferredAuthMethod.OAuth: {
          // should be unreachable code since we are using the old importer's OAuth flow to trigger the new importer
          let oauthUrl = this.knowledgeHubService.getOAuthURL(importSource.id);
          let window = this.openCenteredWindowService.setup(oauthUrl, 800, 1280);
          if (window) {
            window.focus();
            await this.waitForWindowClose(window);
          }

          await importSource.reload();

          if (importSource.hasCredentials) {
            let isMigration = this.args.isMigrating ?? false;
            await this.knowledgeHubService.startImport(
              importSource.id,
              this.syncBehavior,
              isMigration,
            );
            this.handleSuccessfulSync();
          } else {
            await this.handleFailedSync(importSource);
          }
          break;
        }
        case PreferredAuthMethod.ApiKey: {
          let isMigration = this.args.isMigrating ?? false;
          await this.knowledgeHubService.startImport(
            importSource.id,
            this.syncBehavior,
            isMigration,
          );
          this.handleSuccessfulSync();
          break;
        }
      }
      this.trackZendeskSyncAnalytics();
    } catch (error: any) {
      console.error('Failed to start Zendesk sync with new importer:', error);

      if (error.jqXHR?.status === 409) {
        this.notificationsService.notifyError(
          this.intl.t('knowledge-hub.sync-modal.zendesk.sync-already-in-progress'),
        );
      } else {
        this.notificationsService.notifyError(
          this.intl.t('knowledge-hub.sync-modal.zendesk.sync-failed'),
        );
      }

      if (importSource && error.jqXHR?.status !== 409) {
        try {
          await importSource.remove({ notify: false });
        } catch (cleanupError) {
          console.error('Failed to clean up import source after error:', cleanupError);
        }
      }
    }
  }

  async startSyncWithOldImporter(url: string, newImporter = false) {
    try {
      let oauthState: any = await this.createOauthState(url, newImporter);
      let ZdUrl = await this.zendeskOAuthURL(oauthState.id, url);
      let window = this.openCenteredWindowService.setup(ZdUrl, 800, 1280);
      if (window) {
        window.focus();
        await this.waitForWindowClose(window);
      }
      if (newImporter) {
        // New importer doesn't emit real-time events, so close modal directly
        this.handleSuccessfulSync();
      }
      this.trackZendeskSyncAnalytics();
    } catch (error) {
      console.error('Failed to start Zendesk sync with old importer:', error);
      throw error;
    }
  }

  normalizeZendeskURL(url: string) {
    url = url?.trim();

    if (isEmpty(url)) {
      return;
    }

    if (url.match(/^http:\/\//)) {
      url = url.substring(7);
    }

    if (url.match(/^https/) === null) {
      url = `https://${url}`;
    }

    let HELP_CENTER_REGEX = /\/hc/;
    // This conditional will strip out the help center centric part of the URL
    if (url.match(HELP_CENTER_REGEX)) {
      let index = url.match(HELP_CENTER_REGEX)?.index;
      url = url.substring(0, index);
    }

    return url;
  }

  async createOauthState(url: string, newImporter = false) {
    let oauthState = this.store.createRecord('articles/oauth-state', {
      siteUrl: url,
      helpCenterId: this.selectedHelpCenter?.id,
      newImporter,
    });
    return oauthState.save();
  }

  async zendeskOAuthURL(oauthStateId: string, url: string) {
    let state: Record<string, unknown> = {
      oauth_state_id: oauthStateId,
      mode: this.sync ? 'sync' : 'import',
    };

    if (this.syncPrivateArticles) {
      state.sync_private_articles = this.syncPrivateArticles;
    }

    if (this.syncStaffArticles) {
      state.sync_staff_articles = this.syncStaffArticles;
    }

    let stateJson = JSON.stringify(state);

    let redirectUri = this.shouldAlwaysUseIntercomRedirectUri
      ? `https://${this.regionService.appHost}/auth/articles-importer/zendesk/callback`
      : this.selectedHelpCenter?.zendeskImporterRedirectUri;
    let clientId = this.selectedHelpCenter?.zendeskImporterClientId;

    let queryParams = encodeURI(
      `response_type=code&redirect_uri=${redirectUri}&client_id=${clientId}&scope=hc:read&state=${stateJson}`,
    );

    return `${url}/oauth/authorizations/new?${queryParams}`;
  }

  private handleSuccessfulSync() {
    let notificationMessage =
      this.syncBehavior === SyncBehaviorType.Sync
        ? this.intl.t('knowledge-hub.sync-modal.zendesk.sync-started')
        : this.intl.t('knowledge-hub.sync-modal.zendesk.import-started');

    this.notificationsService.notifyConfirmation(notificationMessage);
    this.args.onModalClose();
  }

  private async handleFailedSync(importSource: any) {
    try {
      await importSource.remove({ notify: false });
    } catch (error) {
      console.error('Failed to clean up import source after OAuth failure:', error);
    }
    this.notificationsService.notifyError(
      this.intl.t('knowledge-hub.sync-modal.zendesk.authentication-failed'),
    );
    this.args.onModalClose();
  }

  private trackZendeskSyncAnalytics() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      context: 'import-content',
      object: `import-content.zendesk`,
      section: 'knowledge-hub.new-content.zendesk',
      place: 'knowledge-hub.new-content',
    });
  }

  private waitForWindowClose(window: any): Promise<void> {
    return new Promise((resolve) => {
      let checkInterval = setInterval(() => {
        if (window.closed) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 200);
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::SetupModal::SyncModal::Zendesk': typeof ZendeskSyncModal;
    'knowledge-hub/setup-modal/sync-modal/zendesk': typeof ZendeskSyncModal;
  }
}
